import React from 'react';

import { Button, Title } from '@skillarea/lib-frontend';
import { useAdaptive } from '@skillarea/lib-frontend';
import { GameReturnDto } from '@skillarea/lib-shared';
import { useNavigate } from '@tanstack/react-router';
import { Trans, useTranslation } from 'react-i18next';

import { Container } from '../Container/Container';
import { GamesSection } from '../GamesSection/GamesSection';

import {
  AntiCheatSystemFeature,
  FairCompetitionsFeature,
  InstantPayoutsFeature,
  QuickMatchStartFeature,
} from './Feature';
import heroBgImg from './imgs/hero-bg-left.webp';
import heroRightAnimatedImg from './imgs/hero-bg-right-animated.webp';
import heroRightPosterImg from './imgs/hero-bg-right-poster.webp';
import robotImg from './imgs/robot.webp';
import styles from './Landing.module.scss';

import { openTawkChat } from '~/lib/tawk';

interface ILandingProps {
  isLoading?: boolean;
  gamesData?: { items: Record<number, GameReturnDto> } | undefined;
}

export const Landing: React.FC<ILandingProps> = ({
  gamesData,
  isLoading = false,
}) => {
  const { t } = useTranslation('landing');
  const navigate = useNavigate();

  const handleSignup = () => {
    navigate({ to: '.', hash: 'register' });
  };

  const { isMobile } = useAdaptive();

  return (
    <div className={styles.landingContainer}>
      <section className={styles.hero}>
        <div className={styles.heroBackground}>
          <img alt="" className={styles.heroBg} src={heroBgImg} />
        </div>
        <Container className={styles.heroContainer}>
          <div className={styles.heroText}>
            <h1>
              {t('hero.titleLine1')}
              <br />
              {t('hero.titleLine2')}
              <br />
              {t('hero.titleLine3')}
            </h1>
            <p>
              {t('hero.subtitleLine1')}
              <br />
              {t('hero.subtitleLine2')}
            </p>

            <div className={styles.heroActions}>
              <Button
                className={styles.heroSignupButton}
                color="primary"
                onClick={handleSignup}
              >
                {t('hero.signup')}
              </Button>
            </div>
          </div>

          <picture>
            <source
              className={styles.heroRight}
              media="(prefers-reduced-motion: no-preference)"
              srcSet={heroRightAnimatedImg}
              type="image/webp"
            />
            <img alt="" className={styles.heroRight} src={heroRightPosterImg} />
          </picture>
        </Container>
      </section>

      <section className={styles.platformDescription}>
        <Container>
          <div>
            <Title
              as="h2"
              className={styles.platformTitle}
              color="contrast"
              size="l"
              variant="benzin"
            >
              <Trans
                components={{
                  quick: (
                    <Title
                      className={styles.quick}
                      color="accent"
                      isInsertion
                      size="l"
                      variant="cormorant"
                    />
                  ),
                  strategy: (
                    <Title
                      className={styles.strategy}
                      color="orange"
                      isInsertion
                      size="l"
                    />
                  ),
                  br: <br />,
                }}
                i18nKey="platform.title"
                ns="landing"
              />
            </Title>
          </div>
        </Container>
        <div className={styles.games}>
          <GamesSection
            gamesData={gamesData}
            gridClassName={styles.gamesGrid}
            isLoading={isLoading}
          />
        </div>
      </section>

      <section className={styles.reasons}>
        <Container>
          <Title as="h2" color="text" size="l" variant="benzin">
            <Trans
              components={{
                playing: (
                  <Title
                    color="text"
                    isInsertion
                    size="m"
                    variant="cormorant"
                  />
                ),
                br: <br />,
              }}
              i18nKey="reasons.title"
              ns="landing"
            />
          </Title>
        </Container>
      </section>

      <section className={styles.features}>
        <Container>
          <div className={styles.featuresGrid}>
            <FairCompetitionsFeature />
            <InstantPayoutsFeature />
            <QuickMatchStartFeature />
            <AntiCheatSystemFeature />
          </div>
        </Container>
      </section>

      <section className={styles.cta}>
        <Container>
          <div className={styles.ctaContent}>
            <div className={styles.ctaLeft}>
              <img
                alt={t('cta.robotAlt')}
                className={styles.ctaImage}
                src={robotImg}
              />
            </div>

            <div className={styles.ctaRight}>
              <Title
                className={styles.ctaTitle}
                color="contrast"
                line="100"
                size={isMobile ? 'xs' : 's'}
                variant="benzin"
              >
                {t('cta.titleMain')}
              </Title>
              <Title
                color="accent"
                line="100"
                size={isMobile ? 'xs' : 's'}
                variant="cormorant"
              >
                {t('cta.titleAccent')}
              </Title>
              <ul className={styles.ctaDescription}>
                <li>{t('cta.bullet1')}</li>
                <br />
                <li>{t('cta.bullet2')}</li>
                <br />
                <li>{t('cta.bullet3')}</li>
              </ul>

              <h4 className={styles.ctaSupport}>
                <Trans
                  components={{ br: <br /> }}
                  i18nKey="cta.support"
                  ns="landing"
                />
              </h4>
              <button
                className={styles.chatButton}
                onClick={openTawkChat}
                type="button"
              >
                {t('cta.chatButton')}
              </button>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
};
